import Promise from 'bluebird';
import _ from 'underscore';
import { loadScriptAsynchronously } from '@bingads-webui-campaign-react/smart-component-util';

const fbSdkScriptId = 'facebook-js-sdk';
const fbSdkUrl = 'https://connect.facebook.net/en_US/sdk.js';

const getFacebookJsSdk = () => {
  const { FB } = window;

  if (_.isUndefined(FB) || _.isUndefined(FB.init)) {
    return null;
  }

  return FB;
};

/**
 * Poll until the Facebook JS SDK is available on this page.
 * @param {Number} [maxAttempts=10] - Max number of attempts.
 * @param {Number} [interval=100] - Time in ms to wait between attempts.
 * @param {Number} [currentAttempts=0] - Current number of attempts.
 * @returns {Promise} - Promise that resolves to the Facebook SDK or null if it failed to load.
 */
const pollForFacebookJsSdk = (maxAttempts = 10, interval = 100, currentAttempts = 0) => {
  const sdk = getFacebookJsSdk();

  if (!sdk) {
    if (currentAttempts >= maxAttempts) {
      // reached max attempts
      return Promise.resolve(null);
    }

    // delay and then poll again
    return Promise.delay(interval)
      .then(() => pollForFacebookJsSdk(maxAttempts, interval, currentAttempts + 1));
  }

  return Promise.resolve(sdk);
};

export const facebookInit = (DMCAppId, logger, handleFacebookSDKError) => {
  logger.logTrace('Initializing Facebook SDK');

  // TODO: DMC will provide an appId in development mode,
  // For dev, we all have no permissions to that fb app.
  // So just use DMC SI appId for our local env here,
  // Will follow up with DMC for the permissions and remove here.
  let appId = DMCAppId;
  const localEnvPattern = /^(app1\.)?(beta\.)?ui\.ads-int\.microsoft\.com$/;
  const localEnvCCPattern = /^(app1\.)?(beta\.)?ads-int\.microsoft\.com$/;
  if (document.domain.match(localEnvPattern) || document.domain.match(localEnvCCPattern)) {
    appId = '647456452727479';
  }

  // setup the initialization callback
  if (!window.fbAsyncInit) {
    window.fbAsyncInit = () => {
      window.FB.init({
        appId,
        status: true,
        xfbml: false,
        version: 'v8.0',
      });

      logger.logTrace('Successfully initialized Facebook SDK');
    };
  }

  return loadScriptAsynchronously(fbSdkScriptId, fbSdkUrl, logger)
    .then(pollForFacebookJsSdk)
    .then((sdk) => {
      if (sdk) {
        logger.logTrace('Successfully loaded Facebook SDK');
      } else {
        logger.logTrace('Failed to loaded Facebook SDK, SDK is empty');
        throw new Error('Facebook SDK is empty');
      }

      return sdk;
    })
    .error((err) => {
      logger.logTrace(`Error while loading Facebook SDK: ${err}`);

      // Try to get the sdk and retrieve more logs for investigating sdk load timeout
      fetch(fbSdkUrl)
        .then((res) => {
          if (res.ok) {
            logger.logTrace('Refetch fb sdk succeed.');
          } else {
            logger.logTrace(`Refetch fb sdk failed. status: ${res.status} statusText: ${res.statusText}`);
          }
        });

      if (document.getElementById(fbSdkScriptId)) {
        logger.logTrace('Removing Facebook SDK script due to error.');
        document.getElementById(fbSdkScriptId).remove();
      }

      handleFacebookSDKError();
    });
};

export const facebookLogin = (onSuccess, onFail, logger, fbAppId, handleFacebookSDKError) => {
  logger.logTrace('Trying to login Facebook account');

  facebookInit(fbAppId, logger, handleFacebookSDKError)
    .then((sdk) => {
      sdk.getLoginStatus((loginStatus) => {
        if (loginStatus.status === 'connected') {
          onSuccess(loginStatus);
          logger.logTrace('Facebook account already logged in');
        } else {
          const permissions = 'pages_read_engagement, pages_read_user_content, business_management, pages_manage_ads';
          const loginOptions = {
            scope: permissions,
            return_scopes: true,
          };

          try {
            window.FB.login(
              (fbInfo) => {
                onSuccess(fbInfo);
                logger.logTrace('Successfully logged in Facebook account');
              },
              loginOptions
            );
          } catch (response) {
            if (response.error) {
              onFail({
                error: response.error,
              });

              logger.logTrace(`Error while login Facebook account: ${response.error}`);
              return;
            }

            onFail({
              error: 'AUTH_ERROR',
            });
            logger.logTrace('Error while login Facebook account');
          }
        }
      });
    })
    .catch((error) => {
      logger.logTrace(`Error while login Facebook account. ${JSON.stringify(error)}`);
    });
};

export const facebookLogout = (onLogoutSuccess, logger, fbAppId, handleFacebookSDKError) => {
  logger.logTrace('Trying to logout Facebook account');

  facebookInit(fbAppId, logger, handleFacebookSDKError)
    .then((sdk) => {
      sdk.getLoginStatus((loginStatus) => {
        if (loginStatus.status === 'connected') {
          // For some reason, the callback in logout has been executed twice
          // add debounce for workaround
          const debouncedOnLogoutSucceed = _.debounce(() => {
            onLogoutSuccess();
            logger.logTrace('Successfully logout Facebook account');
          }, 10);
          window.FB.logout(debouncedOnLogoutSucceed);
        } else {
          onLogoutSuccess();
          logger.logTrace('Facebook account is not logged in, skip logout');
        }
      });
    })
    .catch((error) => {
      logger.logTrace(`Error while logout Facebook account. ${JSON.stringify(error)}`);
    });
};
